import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import InvoiceEmailer from './InvoiceEmailer'
import { sendInvoiceEmail } from '../../supabase-client'

/**
 * SendInvoiceEmailDialog Component (US-P4-001 Task 4 v2.0)
 *
 * Dialog wrapper for sending invoices via Xero email API.
 * Leverages Xero's professional email templates, payment links, and automatic reminders.
 *
 * Features:
 * - Send invoices via Xero API (xero-send-invoice Edge Function)
 * - Xero handles email delivery, templates, and payment tracking
 * - Loading and error state management
 * - Success/failure feedback
 * - Automatic invoice status updates (sent_via_xero flag)
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback to handle dialog open/close
 * @param {Object} props.invoice - Invoice object with all details
 * @param {Object} props.customer - Customer object with email and name
 * @param {Function} props.onEmailSent - Callback after successful email send
 */
export default function SendInvoiceEmailDialog({
  open,
  onOpenChange,
  invoice,
  customer,
  onEmailSent
}) {
  const [isSending, setIsSending] = useState(false)
  const [sendStatus, setSendStatus] = useState(null)

  const handleSendEmail = async () => {
    setIsSending(true)
    setSendStatus(null)

    try {
      // Call sendInvoiceEmail (alias to sendInvoiceViaXero) - US-P4-001 Task 4 v2.0
      const data = await sendInvoiceEmail(invoice.id)

      if (data.success) {
        setSendStatus({ success: true, error: null })

        // Optionally call the onEmailSent callback after a short delay
        // to allow user to see success message
        setTimeout(() => {
          if (onEmailSent) {
            onEmailSent()
          }
        }, 2000)
      } else {
        throw new Error(data.error || 'Failed to send email')
      }
    } catch (error) {
      console.error('Error sending invoice email:', error)
      setSendStatus({
        success: false,
        error: error.message || 'An unexpected error occurred'
      })
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Invoice via Xero</DialogTitle>
        </DialogHeader>

        <InvoiceEmailer
          invoice={invoice}
          customer={customer}
          branch={invoice.branch}
          onSendEmail={handleSendEmail}
          isSending={isSending}
          sendStatus={sendStatus}
        />
      </DialogContent>
    </Dialog>
  )
}
